import React, { useState, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { Form } from 'react-bootstrap';

/**
 * ProductSearchBar - Search input that triggers BigQuery-backed search
 *
 * This is a controlled input that syncs with parent state and debounces
 * API calls. The actual search is performed server-side via productSearch.
 *
 * @param {Object} props
 * @param {Function} props.onSearchChange - Callback when search term changes (receives searchTerm string)
 * @param {boolean} props.isLoading - Whether search results are currently loading
 * @param {string} props.value - Current search value (controlled)
 * @param {React.RefObject} props.gridRef - Reference to AG-Grid (optional)
 */
export const ProductSearchBar = ({ onSearchChange, isLoading, value = '', gridRef }) => {
    // Local state for immediate input response
    const [inputValue, setInputValue] = useState(value);

    // Sync local state when parent value changes (e.g., on navigation/reset)
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Use ref to always call the latest onSearchChange without recreating debounce.
    // This prevents the debounced call from being silently dropped when
    // filter/sort changes cause onSearchChange to get a new identity.
    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => { onSearchChangeRef.current = onSearchChange; }, [onSearchChange]);

    // Stable debounced function — never recreated, so pending calls are never lost
    const debouncedOnChange = useCallback(
        debounce((searchTerm) => {
            onSearchChangeRef.current?.(searchTerm);
        }, 350),
        []
    );

    // Cancel debounce on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    function handleInputChange(event) {
        const newValue = event.target.value;
        setInputValue(newValue);  // Update local state immediately for responsive UI
        debouncedOnChange(newValue);  // Debounce the API call
    }

    // Clear search when user presses Escape
    function handleKeyDown(event) {
        if (event.key === 'Escape') {
            setInputValue('');
            onSearchChange?.('');  // Immediate clear, no debounce needed
        }
    }

    return (
        <div style={{ position: 'relative' }}>
            <Form.Control
                type="text"
                value={inputValue}
                placeholder={isLoading ? 'Searching...' : 'Search for a product...'}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                style={{
                    height: '25px',
                    borderRadius: '15px',
                    paddingRight: '50px'
                }}
                className='w-100 border-0 fs--1 ms-2'
            />
        </div>
    );
};

export default ProductSearchBar;
